import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/react';


export function trackGA4Event(event, params = {}) {
    TagManager.dataLayer({ dataLayer: { event, ...params } });
}

// Same as trackGA4Event, but for call sites that need to know the event
// actually went out before doing something else (e.g. redirecting right
// after signup, where a page navigation can cancel the network request
// mid-flight). GTM invokes eventCallback once tags bound to this event have
// fired, falling back to eventTimeout (ms) if a tag never calls back — the
// returned promise resolves either way. Several calls can be joined with
// Promise.all instead of nesting them, which would otherwise stack each
// call's fallback timeout on top of the others.
//
// This promise never rejects: a broken/blocked dataLayer (ad blockers,
// privacy extensions) must not be able to strand a user who has already
// been fully signed up, just because the redirect that follows is gated on
// this resolving. Instead it resolves with a boolean: true if GTM actually
// confirmed the tag fired, false if it timed out or errored (reported to
// Sentry here) — callers can inspect this to surface tracking failures
// without blocking on them.
// 2000ms, not 1000ms: a production event showed the full GTM chain (load
// container -> evaluate trigger -> dispatch hit) taking just over 1s on its
// own, with no other page activity competing for it — 1s wasn't a fair
// window for the normal case, not just a congested one.
const GA4_CONFIRM_TIMEOUT_MS = 2000;

export function trackGA4EventAsync(event, params = {}) {
    return new Promise((resolve) => {
        try {
            let settled = false;
            const settle = (success) => { if (!settled) { settled = true; resolve(success); } };
            TagManager.dataLayer({
                dataLayer: { event, ...params, eventCallback: () => settle(true), eventTimeout: GA4_CONFIRM_TIMEOUT_MS },
            });
            setTimeout(() => settle(false), GA4_CONFIRM_TIMEOUT_MS);
        } catch (error) {
            Sentry.captureException(error);
            resolve(false);
        }
    });
}

// Just gets the values into the dataLayer — GTM still needs a tag mapping
// these onto GA4's actual User Properties fields, or they'll keep showing
// as (not set) even after this fires.
export function setGA4UserProperties(properties) {
    TagManager.dataLayer({ dataLayer: { event: 'set_user_properties', ...properties } });
}

// Shared by the Google and email signup redirects: fire several
// trackGA4EventAsync calls in parallel (never nested — nesting stacks each
// call's own fallback timeout on top of the others), then hand off to the
// caller to actually redirect once they've all settled (confirmed or timed
// out — the redirect always proceeds either way, this never blocks it).
// A miss is logged to the console only (not Sentry) — this happens too often
// in the normal, expected case to be error-tracking noise, but console.warn
// still shows up in a live debugging session and in session-replay tools'
// console breadcrumbs if that recording gets reviewed later.
// trackGA4EventAsync never rejects, so onError only runs for an unexpected
// error in onSettled itself — callers use it to recover (toast + reset auth
// state) instead of leaving the user stuck mid-redirect.
export function gateRedirectOnGA4Events(eventPromises, warnMessage, onSettled, onError) {
    return Promise.all(eventPromises)
        .then((results) => {
            if (!results.every(Boolean)) {
                console.warn(warnMessage);
            }
            onSettled();
        })
        .catch((error) => {
            Sentry.captureException(error);
            onError(error);
        });
}
